import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';

const ConfiguracionCorreos = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [emailConfig, setEmailConfig] = useState({
    smtpServer: 'smtp.gmail.com',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: 'CONEM Sistema',
    useSSL: true,
    useTLS: true
  });

  const [notificationConfig, setNotificationConfig] = useState({
    newQuotationNotification: true,
    quotationStatusChange: true,
    userRegistration: false,
    systemAlerts: true,
    dailyReports: false,
    weeklyReports: true,
    emailFrequency: 'immediate',
    recipientEmails: ['admin@conem.com']
  });

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Simular carga de configuración desde el backend
      // En un caso real, harías llamadas a la API
      // const emailResponse = await api.get('/config/email');
      // const notificationResponse = await api.get('/config/notifications');
      
      // Por ahora, mantenemos los valores por defecto
      setSuccess('Configuración cargada correctamente');
    } catch (err) {
      setError('Error al cargar la configuración');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailConfigChange = (field, value) => {
    setEmailConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationConfigChange = (field, value) => {
    setNotificationConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRecipientEmailsChange = (emails) => {
    const emailList = emails.split(',').map(email => email.trim()).filter(email => email);
    setNotificationConfig(prev => ({
      ...prev,
      recipientEmails: emailList
    }));
  };

  const testEmailConnection = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Simular prueba de conexión
      // await api.post('/config/email/test', emailConfig);
      
      setTimeout(() => {
        setSuccess('Conexión de correo probada exitosamente');
        setSaving(false);
      }, 2000);
    } catch (err) {
      setError('Error al probar la conexión de correo');
      setSaving(false);
    }
  };

  const saveConfiguration = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Simular guardado de configuración
      // await api.put('/config/email', emailConfig);
      // await api.put('/config/notifications', notificationConfig);
      
      setTimeout(() => {
        setSuccess('Configuración guardada exitosamente');
        setSaving(false);
      }, 1500);
    } catch (err) {
      setError('Error al guardar la configuración');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '95%',
      maxWidth: '1400px',
      mx: 'auto',
      mt: 2,
      mb: 2,
      background: '#fff',
      borderRadius: 4,
      boxShadow: '0 4px 32px rgba(0,0,0,0.10)',
      p: 4,
    }}>
      {/* Header */}
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            color: '#222',
            fontFamily: "'Fira Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '"📧"',
              marginRight: 2,
              fontSize: '1.2em'
            }
          }}
        >
          Configuración de Correos y Notificaciones
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadConfiguration}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          Recargar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Email Configuration Section */}
      <Box sx={{ 
        mb: 4, 
        p: 3, 
        backgroundColor: '#f8f9fa', 
        borderRadius: 3, 
        border: '1px solid #e9ecef' 
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3, 
            color: '#2c3e50', 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '"⚙️"',
              marginRight: 2,
              fontSize: '1.2em'
            }
          }}
        >
          Configuración del Servidor de Correo
        </Typography>
        
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Servidor SMTP"
              value={emailConfig.smtpServer}
              onChange={(e) => handleEmailConfigChange('smtpServer', e.target.value)}
              variant="outlined"
              helperText="Ejemplo: smtp.gmail.com"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Puerto SMTP"
              value={emailConfig.smtpPort}
              onChange={(e) => handleEmailConfigChange('smtpPort', e.target.value)}
              variant="outlined"
              helperText="Ejemplo: 587 para TLS, 465 para SSL"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Usuario SMTP"
              value={emailConfig.smtpUser}
              onChange={(e) => handleEmailConfigChange('smtpUser', e.target.value)}
              variant="outlined"
              helperText="Tu dirección de correo electrónico"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Contraseña SMTP"
              type="password"
              value={emailConfig.smtpPassword}
              onChange={(e) => handleEmailConfigChange('smtpPassword', e.target.value)}
              variant="outlined"
              helperText="Contraseña de aplicación recomendada"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Email remitente"
              value={emailConfig.fromEmail}
              onChange={(e) => handleEmailConfigChange('fromEmail', e.target.value)}
              variant="outlined"
              helperText="Email que aparecerá como remitente"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Nombre remitente"
              value={emailConfig.fromName}
              onChange={(e) => handleEmailConfigChange('fromName', e.target.value)}
              variant="outlined"
              helperText="Nombre que aparecerá como remitente"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={emailConfig.useSSL}
                  onChange={(e) => handleEmailConfigChange('useSSL', e.target.checked)}
                />
              }
              label="Usar SSL"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={emailConfig.useTLS}
                  onChange={(e) => handleEmailConfigChange('useTLS', e.target.checked)}
                />
              }
              label="Usar TLS"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<SecurityIcon />}
            onClick={testEmailConnection}
            disabled={saving}
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            Probar Conexión
          </Button>
        </Box>
      </Box>

      {/* Notifications Configuration Section */}
      <Box sx={{ 
        mb: 4, 
        p: 3, 
        backgroundColor: '#f8f9fa', 
        borderRadius: 3, 
        border: '1px solid #e9ecef' 
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3, 
            color: '#2c3e50', 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '"🔔"',
              marginRight: 2,
              fontSize: '1.2em'
            }
          }}
        >
          Configuración de Notificaciones
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Eventos del Sistema
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationConfig.newQuotationNotification}
                      onChange={(e) => handleNotificationConfigChange('newQuotationNotification', e.target.checked)}
                    />
                  }
                  label="Nueva cotización creada"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationConfig.quotationStatusChange}
                      onChange={(e) => handleNotificationConfigChange('quotationStatusChange', e.target.checked)}
                    />
                  }
                  label="Cambio de estado de cotización"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationConfig.userRegistration}
                      onChange={(e) => handleNotificationConfigChange('userRegistration', e.target.checked)}
                    />
                  }
                  label="Registro de nuevo usuario"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationConfig.systemAlerts}
                      onChange={(e) => handleNotificationConfigChange('systemAlerts', e.target.checked)}
                    />
                  }
                  label="Alertas del sistema"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Reportes Automáticos
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationConfig.dailyReports}
                      onChange={(e) => handleNotificationConfigChange('dailyReports', e.target.checked)}
                    />
                  }
                  label="Reportes diarios"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationConfig.weeklyReports}
                      onChange={(e) => handleNotificationConfigChange('weeklyReports', e.target.checked)}
                    />
                  }
                  label="Reportes semanales"
                />
                
                <Divider sx={{ my: 2 }} />
                
                <FormControl fullWidth margin="normal">
                  <InputLabel>Frecuencia de notificaciones</InputLabel>
                  <Select
                    value={notificationConfig.emailFrequency}
                    label="Frecuencia de notificaciones"
                    onChange={(e) => handleNotificationConfigChange('emailFrequency', e.target.value)}
                  >
                    <MenuItem value="immediate">Inmediata</MenuItem>
                    <MenuItem value="hourly">Cada hora</MenuItem>
                    <MenuItem value="daily">Diaria</MenuItem>
                    <MenuItem value="weekly">Semanal</MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Correos destinatarios"
              value={notificationConfig.recipientEmails.join(', ')}
              onChange={(e) => handleRecipientEmailsChange(e.target.value)}
              variant="outlined"
              helperText="Separar múltiples correos con comas"
              multiline
              rows={2}
            />
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {notificationConfig.recipientEmails.map((email, index) => (
                <Chip
                  key={index}
                  label={email}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={saveConfiguration}
          disabled={saving}
          sx={{
            px: 4,
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1.1rem'
          }}
        >
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </Box>
    </Box>
  );
};

export default ConfiguracionCorreos;
